/**
 * React Integration for Effect Repository
 * Provides hooks to use Effect-based services in React components
 */

import * as Effect from 'effect/Effect';
import * as Runtime from 'effect/Runtime';
import * as Layer from 'effect/Layer';
import * as Fiber from 'effect/Fiber';
import { useEffect, useState, useRef, useMemo } from 'react';
import { RepositoryServicesLayer } from '@/services/repository-layers';

/**
 * Global memoized runtime with all repository services
 * This ensures all hooks share the same service instances (including PubSub)
 */
let globalRuntime: Runtime.Runtime<any> | null = null;
let runtimePromise: Promise<Runtime.Runtime<any>> | null = null;

/**
 * Initialize the runtime asynchronously
 * This must be called before using any repository hooks
 */
async function initializeRuntime(): Promise<Runtime.Runtime<any>> {
  if (globalRuntime) return globalRuntime;

  if (!runtimePromise) {
    runtimePromise = Effect.runPromise(
      Effect.scoped(Layer.toRuntime(RepositoryServicesLayer))
    ).then(runtime => {
      globalRuntime = runtime;
      return runtime;
    });
  }

  return runtimePromise;
}

/**
 * Get the global runtime (synchronously)
 * Throws if runtime hasn't been initialized yet
 */
export function getRepositoryRuntime(): Runtime.Runtime<any> {
  if (!globalRuntime) {
    throw new Error('[Runtime] Runtime not initialized. Call initializeRuntime() first or use getRepositoryRuntimeAsync()');
  }
  return globalRuntime;
}

/**
 * Get or create the runtime asynchronously
 */
export async function getRepositoryRuntimeAsync(): Promise<Runtime.Runtime<any>> {
  return initializeRuntime();
}

/**
 * Execute an Effect program and return a Promise
 * Uses the shared global runtime to ensure service instance consistency
 */
export async function runEffect<A, E>(
  effect: Effect.Effect<A, E, any>
): Promise<A> {
  const runtime = await getRepositoryRuntimeAsync();
  return Runtime.runPromise(runtime)(effect);
}

/**
 * Helper to run an effect as a fiber and return cleanup function
 * Useful for subscriptions in useEffect
 */
export async function runEffectFork<A, E>(
  effect: Effect.Effect<A, E, any>
): Promise<() => void> {
  const runtime = await getRepositoryRuntimeAsync();
  const fiber = Runtime.runFork(runtime)(effect);
  return () => {
    Runtime.runFork(runtime)(Fiber.interrupt(fiber));
  };
}

/**
 * Hook to run an Effect and return the result
 * Automatically handles loading and error states
 */
export function useEffect_<A, E>(
  effect: Effect.Effect<A, E, any>,
  deps: React.DependencyList
): {
  data: A | null;
  error: E | null;
  isLoading: boolean;
  refetch: () => void;
} {
  const [data, setData] = useState<A | null>(null);
  const [error, setError] = useState<E | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);

  const execute = useMemo(
    () => () => {
      setIsLoading(true);
      setError(null);

      runEffect(effect)
        .then((result) => {
          if (mountedRef.current) {
            setData(result);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          if (mountedRef.current) {
            setError(err);
            setIsLoading(false);
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps
  );

  useEffect(() => {
    mountedRef.current = true;
    execute();

    return () => {
      mountedRef.current = false;
    };
  }, [execute]);

  return {
    data,
    error,
    isLoading,
    refetch: execute,
  };
}

/**
 * Hook to run an Effect mutation (like useMutation from React Query)
 * Returns a function to execute the effect and the current state
 */
export function useEffectMutation<A, E, Input>(
  effectFn: (input: Input) => Effect.Effect<A, E, any>
): {
  mutate: (input: Input) => void;
  mutateAsync: (input: Input) => Promise<A>;
  data: A | null;
  error: E | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  reset: () => void;
} {
  const [data, setData] = useState<A | null>(null);
  const [error, setError] = useState<E | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const mutateAsync = async (input: Input): Promise<A> => {
    setIsLoading(true);
    setError(null);
    setIsSuccess(false);
    setIsError(false);

    try {
      const result = await runEffect(effectFn(input));
      setData(result);
      setIsSuccess(true);
      setIsLoading(false);
      return result;
    } catch (err) {
      setError(err as E);
      setIsError(true);
      setIsLoading(false);
      throw err;
    }
  };

  const mutate = (input: Input) => {
    mutateAsync(input).catch(() => {
      // Error already handled in state
    });
  };

  const reset = () => {
    setData(null);
    setError(null);
    setIsLoading(false);
    setIsSuccess(false);
    setIsError(false);
  };

  return {
    mutate,
    mutateAsync,
    data,
    error,
    isLoading,
    isSuccess,
    isError,
    reset,
  };
}
